import { compact, uniq } from "es-toolkit/array";
import get from "es-toolkit/compat/get";
import set from "es-toolkit/compat/set";
import {
  buildFields,
  isRecord,
  toDatasetRows,
  toNumber,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

export type BindingPath = string | readonly (string | number)[];

type BuildFieldEntry = Parameters<typeof buildFields>[0][number];

type MetricUnit = BuildFieldEntry["unit"];
type MetricFormat = BuildFieldEntry["format"];

type ReadValue<T> = (value: unknown) => T | null;

type SharedMetricFieldBlueprint = {
  aliases?: string[];
  unit?: MetricUnit;
  format?: MetricFormat;
  exportable?: BuildFieldEntry["exportable"];
  defaultVisible?: BuildFieldEntry["defaultVisible"];
  internal?: BuildFieldEntry["internal"];
  lineInterpolation?: BuildFieldEntry["lineInterpolation"];
  metricFamily?: BuildFieldEntry["metricFamily"];
  movingAverageWindowDays?: BuildFieldEntry["movingAverageWindowDays"];
};

export type MetricBlueprint = {
  key: string;
  unit?: SharedMetricFieldBlueprint["unit"];
  format?: SharedMetricFieldBlueprint["format"];
  lineInterpolation?: SharedMetricFieldBlueprint["lineInterpolation"];
  metricFamily?: SharedMetricFieldBlueprint["metricFamily"];
  movingAverageWindowDays?: SharedMetricFieldBlueprint["movingAverageWindowDays"];
  aggregation?: BuildFieldEntry["aggregation"];
  exportable?: SharedMetricFieldBlueprint["exportable"];
  defaultVisible?: SharedMetricFieldBlueprint["defaultVisible"];
  internal?: SharedMetricFieldBlueprint["internal"];
  aliases?: string[];
  sourceKey?: string;
  sourcePaths?: BindingPath[];
};

export type CompareMetricBlueprint = {
  sourceKey: string;
  currentKey: string;
  previousKey: string;
  unit?: SharedMetricFieldBlueprint["unit"];
  format?: SharedMetricFieldBlueprint["format"];
  lineInterpolation?: SharedMetricFieldBlueprint["lineInterpolation"];
  metricFamily?: SharedMetricFieldBlueprint["metricFamily"];
  movingAverageWindowDays?: SharedMetricFieldBlueprint["movingAverageWindowDays"];
  exportable?: SharedMetricFieldBlueprint["exportable"];
  defaultVisible?: SharedMetricFieldBlueprint["defaultVisible"];
  internal?: SharedMetricFieldBlueprint["internal"];
  /**
   * current/previous で同一意味論を使う場合の簡易指定。
   *
   * @remarks
   * 比較率のように current/previous で分子分母が異なる場合は
   * `currentAggregation` / `previousAggregation` を優先して使う。
   */
  aggregation?: BuildFieldEntry["aggregation"];
  currentAggregation?: BuildFieldEntry["aggregation"];
  previousAggregation?: BuildFieldEntry["aggregation"];
  baseAliases?: string[];
  sourcePaths?: BindingPath[];
};

function toPathLabel(path: BindingPath): string {
  if (typeof path === "string") return path;
  return path.map((segment) => String(segment)).join(".");
}

/**
 * metric source path を trim + uniq した安定順で返す。
 */
export function normalizePaths(paths: string[]): string[] {
  return uniq(compact(paths.map((path) => path.trim())));
}

/**
 * 通常 metric 定義へ `sourcePaths` を一括付与する。
 */
export function withMetricPaths(metrics: MetricBlueprint[]): MetricBlueprint[] {
  return metrics.map((metric) => {
    return {
      ...metric,
      sourcePaths: normalizePaths([
        metric.sourceKey ?? metric.key,
        ...(metric.aliases ?? []),
      ]),
    };
  });
}

/**
 * compare metric 定義へ `sourcePaths` を一括付与する。
 */
export function withCompareMetricPaths(
  metrics: CompareMetricBlueprint[],
): CompareMetricBlueprint[] {
  return metrics.map((metric) => {
    return {
      ...metric,
      sourcePaths: normalizePaths([
        metric.sourceKey,
        ...(metric.baseAliases ?? []),
      ]),
    };
  });
}

/**
 * 指定順で path を探索し、最初にパースできた値を返す。
 *
 * path 候補順がそのまま優先順位になるため、常に決定論で解決される。
 */
function readFirstPathValue<T>(args: {
  source: Record<string, unknown>;
  paths: readonly BindingPath[];
  read: ReadValue<T>;
}): { value: T | null; matchedPath: string | null } {
  for (const path of args.paths) {
    const raw = get(args.source, path);
    if (raw === undefined) continue;
    const value = args.read(raw);
    if (value !== null) {
      return { value, matchedPath: toPathLabel(path) };
    }
  }
  return { value: null, matchedPath: null };
}

/**
 * 先頭一致した path の数値値を返す。
 */
export function readFirstPathNumber(args: {
  source: Record<string, unknown>;
  paths: readonly BindingPath[];
}): { value: number | null; matchedPath: string | null } {
  return readFirstPathValue({
    source: args.source,
    paths: args.paths,
    read: toNumber,
  });
}

/**
 * 先頭一致した path の文字列値を返す。
 */
export function readFirstPathString(args: {
  source: Record<string, unknown>;
  paths: readonly BindingPath[];
}): { value: string | null; matchedPath: string | null } {
  return readFirstPathValue({
    source: args.source,
    paths: args.paths,
    read: toString,
  });
}

/**
 * 先頭一致した path の boolean 値を "true"/"false" 文字列へ変換する。
 */
export function readFirstPathBooleanText(args: {
  source: Record<string, unknown>;
  paths: readonly BindingPath[];
}): { value: string | null; matchedPath: string | null } {
  return readFirstPathValue({
    source: args.source,
    paths: args.paths,
    read: (value) =>
      typeof value === "boolean" ? (value ? "true" : "false") : null,
  });
}

/**
 * path で指定した階層に値を設定する。
 *
 * `metrics.clicks.7d.current` のような slot 生成に使う。
 *
 * @remarks
 * path 解決は `es-toolkit` の `set` へ委譲し、自前の文字列パーサを持たない。
 */
export function setValueByPath(
  target: Record<string, unknown>,
  path: BindingPath,
  value: unknown,
): Record<string, unknown> {
  set(target, path, value);
  return target;
}

function buildSharedMetricFieldProps(
  metric: SharedMetricFieldBlueprint,
): Pick<
  BuildFieldEntry,
  | "unit"
  | "format"
  | "exportable"
  | "defaultVisible"
  | "internal"
  | "lineInterpolation"
  | "metricFamily"
  | "movingAverageWindowDays"
> {
  return {
    ...(metric.unit ? { unit: metric.unit } : null),
    ...(metric.aliases?.length
      ? { aliases: normalizePaths(metric.aliases) }
      : null),
    ...(metric.format ? { format: metric.format } : null),
    ...(metric.exportable !== undefined
      ? { exportable: metric.exportable }
      : null),
    ...(metric.defaultVisible !== undefined
      ? { defaultVisible: metric.defaultVisible }
      : null),
    ...(metric.internal !== undefined ? { internal: metric.internal } : null),
    ...(metric.lineInterpolation
      ? { lineInterpolation: metric.lineInterpolation }
      : null),
    ...(metric.metricFamily ? { metricFamily: metric.metricFamily } : null),
    ...(metric.movingAverageWindowDays
      ? { movingAverageWindowDays: metric.movingAverageWindowDays }
      : null),
  };
}

/**
 * 非比較 dataset 用の metric fields 定義を生成する。
 *
 * @remarks
 * moving-average 系は adapter が `metricFamily` と `movingAverageWindowDays`
 * をここで field metadata へ落とし、authoring/validation が raw 系列との対応を
 * 文字列推測なしで再利用できるようにする。
 */
export function buildMetricFieldEntries(
  metrics: MetricBlueprint[],
): BuildFieldEntry[] {
  return metrics.map((metric) => ({
    key: metric.key,
    role: "metric",
    type: "number",
    ...buildSharedMetricFieldProps(metric),
    metricFamily: metric.metricFamily ?? metric.key,
    ...(metric.aggregation ? { aggregation: metric.aggregation } : null),
  }));
}

/**
 * current/previous 比較 dataset 用の metric fields 定義を生成する。
 *
 * @remarks
 * compare dataset は同じ family の current/previous sibling が並ぶため、
 * comparisonRole を field metadata へ固定して authoring repair 時の取り違えを防ぐ。
 */
export function buildCompareMetricFieldEntries(
  metrics: CompareMetricBlueprint[],
): BuildFieldEntry[] {
  return metrics.flatMap((metric) => [
    {
      key: metric.currentKey,
      role: "metric",
      type: "number",
      ...buildSharedMetricFieldProps(metric),
      metricFamily: metric.metricFamily ?? metric.sourceKey,
      comparisonRole: "current" as const,
      ...((metric.currentAggregation ?? metric.aggregation)
        ? { aggregation: metric.currentAggregation ?? metric.aggregation }
        : null),
    },
    {
      key: metric.previousKey,
      role: "metric",
      type: "number",
      ...buildSharedMetricFieldProps(metric),
      metricFamily: metric.metricFamily ?? metric.sourceKey,
      comparisonRole: "previous" as const,
      ...((metric.previousAggregation ?? metric.aggregation)
        ? { aggregation: metric.previousAggregation ?? metric.aggregation }
        : null),
    },
  ]);
}

/**
 * selectableなmetric fieldのうち、異なるfamilyの具体的なpairが2行以上で共存するか判定する。
 *
 * @remarks
 * period comparisonではcurrent/previous siblingが同じfamilyを共有するため、field数ではなく
 * `metricFamily`で2変数を数える。internal fieldはauthoring候補ではないため除外する。
 */
export function hasCoRealizedMetricFamilyPair(args: {
  rows: readonly VizDatasetRow[];
  fields: VizDataset["fields"];
}): boolean {
  const keysByFamily = new Map<string, string[]>();
  for (const field of args.fields) {
    if (
      field.role !== "metric" ||
      field.internal === true ||
      !field.metricFamily
    ) {
      continue;
    }
    const keys = keysByFamily.get(field.metricFamily) ?? [];
    keys.push(field.name);
    keysByFamily.set(field.metricFamily, keys);
  }

  const families = [...keysByFamily.values()];
  return families.some((xKeys, xIndex) =>
    families
      .slice(xIndex + 1)
      .some((yKeys) =>
        xKeys.some((xKey) =>
          yKeys.some(
            (yKey) =>
              args.rows.filter(
                (row) =>
                  typeof row[xKey] === "number" &&
                  Number.isFinite(row[xKey]) &&
                  typeof row[yKey] === "number" &&
                  Number.isFinite(row[yKey]),
              ).length >= 2,
          ),
        ),
      ),
  );
}

/** authoring対象のmetricが少なくとも1件の有限値を持つか判定する。 */
function hasRealizedSelectableMetricValue(args: {
  rows: readonly VizDatasetRow[];
  fields: VizDataset["fields"];
}): boolean {
  const selectableMetricKeys = args.fields
    .filter((field) => field.role === "metric" && field.internal !== true)
    .map((field) => field.name);

  return args.rows.some((row) =>
    selectableMetricKeys.some(
      (key) => typeof row[key] === "number" && Number.isFinite(row[key]),
    ),
  );
}

/**
 * 実データへ投影できるmetricだけをchart catalogへ公開する。
 *
 * @remarks
 * 要求metricが全欠損で内部依存値だけがrowを残した場合、table以外のchartは
 * 有効なdefault bindingを作れないため除外する。要求metricが実現していても
 * 異なるfamilyの有限値pairがなければscatterだけを除外する。
 */
export function resolveMetricChartProfile(args: {
  profile: NonNullable<VizDataset["profile"]>;
  rows: readonly VizDatasetRow[];
  fields: VizDataset["fields"];
  supportsScatterPlot: boolean;
}): NonNullable<VizDataset["profile"]> {
  const hasRealizedMetric = hasRealizedSelectableMetricValue({
    rows: args.rows,
    fields: args.fields,
  });
  const supportedViews = args.profile.supportedViews.filter((view) =>
    hasRealizedMetric
      ? view !== "scatter-plot" || args.supportsScatterPlot
      : view === "table",
  );

  return supportedViews.length === args.profile.supportedViews.length
    ? args.profile
    : { ...args.profile, supportedViews };
}

/**
 * comparison dataset の selectable metric を要求された family に限定する。
 *
 * @remarks
 * 集計のためだけに展開された分子・分母などは row に保持したまま internal にし、
 * capability 判定・authoring・default binding から除外する。
 */
export function constrainMetricFieldsToFamilies(args: {
  fields: VizDataset["fields"];
  metricFamilies: ReadonlySet<string> | null;
}): VizDataset["fields"] {
  if (args.metricFamilies === null) return args.fields;
  const metricFamilies = args.metricFamilies;

  return args.fields.map((field) =>
    field.role === "metric" &&
    field.metricFamily &&
    !metricFamilies.has(field.metricFamily)
      ? {
          ...field,
          internal: true,
          defaultVisible: false,
          exportable: false,
        }
      : field,
  );
}

/** tool metadata の要求 metric 名を dataset の canonical family へ変換する。 */
function resolveRequestedMetricFamilies(
  rawMetrics: unknown,
  metricFamilyByRequestKey: Readonly<Record<string, string>>,
): ReadonlySet<string> | null {
  if (!Array.isArray(rawMetrics)) return null;

  const families = new Set<string>();
  for (const rawMetric of rawMetrics) {
    if (typeof rawMetric !== "string") continue;
    const family = metricFamilyByRequestKey[rawMetric];
    if (family) families.add(family);
  }
  return families.size > 0 ? families : null;
}

/** requestedMetrics 導入前の expanded metrics から保守的に要求 family を復元する。 */
export function resolveRequestedMetricFamiliesWithLegacyFallback(args: {
  expandedMetrics: unknown;
  requestedMetrics: unknown;
  metricFamilyByRequestKey: Readonly<Record<string, string>>;
  defaultMetricKeys: readonly string[];
  dependencyRules: readonly {
    derivedMetric: string;
    dependencyMetrics: readonly string[];
  }[];
}): ReadonlySet<string> | null {
  if (Array.isArray(args.requestedMetrics)) {
    return resolveRequestedMetricFamilies(
      args.requestedMetrics,
      args.metricFamilyByRequestKey,
    );
  }
  if (!Array.isArray(args.expandedMetrics)) return null;

  const inferredMetricSet = new Set(
    args.expandedMetrics.filter(
      (metric): metric is string => typeof metric === "string",
    ),
  );
  const defaultMetricSet = new Set(args.defaultMetricKeys);
  const isCompleteDefaultSet =
    inferredMetricSet.size === defaultMetricSet.size &&
    [...defaultMetricSet].every((metric) => inferredMetricSet.has(metric));

  if (!isCompleteDefaultSet) {
    for (const rule of args.dependencyRules) {
      if (!inferredMetricSet.has(rule.derivedMetric)) continue;
      for (const dependencyMetric of rule.dependencyMetrics) {
        inferredMetricSet.delete(dependencyMetric);
      }
    }
  }

  return resolveRequestedMetricFamilies(
    [...inferredMetricSet],
    args.metricFamilyByRequestKey,
  );
}

function hasFiniteMetricValue(
  rows: readonly VizDatasetRow[],
  key: string,
): boolean {
  return rows.some(
    (row) => typeof row[key] === "number" && Number.isFinite(row[key]),
  );
}

/** selectableかつ有限値を持つmetricからdefault keyを選ぶ。 */
export function resolveRealizedDefaultMetricKey(args: {
  fields: VizDataset["fields"];
  rows: readonly VizDatasetRow[];
  preferredMetricFamily: string;
}): string | null {
  const realizedFields = args.fields.filter(
    (field) =>
      field.role === "metric" &&
      field.internal !== true &&
      hasFiniteMetricValue(args.rows, field.name),
  );

  return (
    realizedFields.find(
      (field) => field.metricFamily === args.preferredMetricFamily,
    )?.name ??
    realizedFields[0]?.name ??
    null
  );
}

/**
 * selectableかつ有限値を持つ比較familyからcurrent/previousのdefault pairを選ぶ。
 *
 * @remarks
 * family内の移動平均だけに値がある場合、値のないbase fieldを初期表示へ戻さない。
 * 同じvariantのcurrent/previous両方に値があるpairを優先し、baseと移動平均、
 * または異なる移動平均窓を初期表示で混在させない。
 */
export function resolveCompareDefaultMetricKeys(args: {
  fields: VizDataset["fields"];
  rows: readonly VizDatasetRow[];
  preferredMetricFamily: string;
}): string[] {
  const selectableFields = args.fields.filter(
    (field) => field.role === "metric" && field.internal !== true,
  );
  const realizedFamilies = new Set(
    selectableFields
      .filter((field) => hasFiniteMetricValue(args.rows, field.name))
      .map((field) => field.metricFamily)
      .filter((family): family is string => Boolean(family)),
  );
  const preferredFamily =
    selectableFields.find(
      (field) =>
        field.metricFamily === args.preferredMetricFamily &&
        realizedFamilies.has(field.metricFamily),
    )?.metricFamily ?? selectableFields[0]?.metricFamily;
  const realizedPreferredFamily =
    preferredFamily && realizedFamilies.has(preferredFamily)
      ? preferredFamily
      : selectableFields.find(
          (field) =>
            field.metricFamily && realizedFamilies.has(field.metricFamily),
        )?.metricFamily;
  if (!realizedPreferredFamily) return [];

  const familyFields = selectableFields.filter(
    (field) => field.metricFamily === realizedPreferredFamily,
  );
  const realizedFamilyFields = familyFields.filter((field) =>
    hasFiniteMetricValue(args.rows, field.name),
  );
  const comparisonRoleOrder = { current: 0, previous: 1 } as const;
  const realizedVariantFields = new Map<
    number | undefined,
    typeof realizedFamilyFields
  >();
  for (const field of realizedFamilyFields) {
    const variantFields =
      realizedVariantFields.get(field.movingAverageWindowDays) ?? [];
    variantFields.push(field);
    realizedVariantFields.set(field.movingAverageWindowDays, variantFields);
  }

  const orderedVariantFields = [...realizedVariantFields.entries()]
    .sort(
      ([leftWindow], [rightWindow]) => (leftWindow ?? 0) - (rightWindow ?? 0),
    )
    .map(([, fields]) => fields);
  const selectedVariantFields =
    orderedVariantFields.find((fields) => {
      const roles = new Set(fields.map((field) => field.comparisonRole));
      return roles.has("current") && roles.has("previous");
    }) ??
    orderedVariantFields[0] ??
    [];

  return selectedVariantFields
    .sort(
      (left, right) =>
        (left.comparisonRole ? comparisonRoleOrder[left.comparisonRole] : 3) -
        (right.comparisonRole ? comparisonRoleOrder[right.comparisonRole] : 3),
    )
    .map((field) => field.name)
    .slice(0, 2);
}

function resolveMetricPaths(metric: {
  sourcePaths?: BindingPath[];
  sourceKey?: string;
  key?: string;
}): BindingPath[] {
  return [
    ...(metric.sourcePaths ?? []),
    ...(metric.sourceKey ? [metric.sourceKey] : []),
    ...(metric.key ? [metric.key] : []),
  ];
}

/**
 * current/previous レコードから compare metric 値を row に設定する。
 */
export function assignCompareMetricValues(args: {
  row: VizDatasetRow;
  current: Record<string, unknown> | null;
  previous: Record<string, unknown> | null;
  metrics: CompareMetricBlueprint[];
}): VizDatasetRow {
  const { row, current, previous, metrics } = args;
  for (const metric of metrics) {
    const paths = resolveMetricPaths({
      sourcePaths: metric.sourcePaths,
      sourceKey: metric.sourceKey,
    });
    row[metric.currentKey] = current
      ? readFirstPathNumber({ source: current, paths }).value
      : null;
    row[metric.previousKey] = previous
      ? readFirstPathNumber({ source: previous, paths }).value
      : null;
  }
  return row;
}

/**
 * 日付を持つ non-compare 時系列 rows を metric blueprint から生成する。
 */
export function buildTimeseriesRowsFromMetrics(args: {
  items: unknown[];
  metrics: MetricBlueprint[];
  dateKey?: string;
}): VizDatasetRow[] {
  const dateKey = args.dateKey ?? "date";
  return toDatasetRows(args.items, (item) => {
    const date = toString(item[dateKey]);
    if (!date) return null;
    const row: VizDatasetRow = { [dateKey]: date };
    for (const metric of args.metrics) {
      row[metric.key] = readFirstPathNumber({
        source: item,
        paths: resolveMetricPaths(metric),
      }).value;
    }
    return row;
  });
}

/**
 * alignCompareTimeseries 結果から compare 時系列 rows を生成する。
 */
export function buildCompareTimeseriesRowsFromMetrics(args: {
  alignedRows: unknown[];
  metrics: CompareMetricBlueprint[];
  dateKey?: string;
  comparingDateKey?: string;
}): VizDatasetRow[] {
  const dateKey = args.dateKey ?? "date";
  const comparingDateKey = args.comparingDateKey ?? "comparingDate";
  return toDatasetRows(args.alignedRows, (item) => {
    const date = toString(item[dateKey]);
    if (!date) return null;
    const current = isRecord(item.currentRow) ? item.currentRow : null;
    const previous = isRecord(item.comparingRow) ? item.comparingRow : null;
    const row: VizDatasetRow = {
      [dateKey]: date,
      [comparingDateKey]: toString(item[comparingDateKey]),
    };
    return assignCompareMetricValues({
      row,
      current,
      previous,
      metrics: args.metrics,
    });
  });
}
